import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getAdminStats } from "../api/admin";
import { AdminStats, AuthProvider } from "../types";
import { AppDispatch } from "../redux/store";
import { addNotification } from "../redux/slices/uiSlice";
import { Card } from "../components/common/Card";
import { Spinner } from "../components/common/Spinner";
import { StatsCard } from "../components/dashboard/StatsCard";
import { formatNumber, formatFileSize } from "../utils/format.utils";
import {
  Users,
  UserCheck,
  Flame,
  HardDrive,
  BarChart3,
  CheckCircle2,
  Zap,
  Clock,
  XCircle,
  Shield,
  TrendingUp,
} from "lucide-react";

export const AdminDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getAdminStats();
        setStats(data);
      } catch (error: any) {
        dispatch(
          addNotification({
            type: "error",
            message:
              error.response?.data?.detail || "Failed to fetch admin stats",
          })
        );
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [dispatch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Failed to load admin statistics</p>
      </div>
    );
  }

  const authProviderLabels: Record<
    AuthProvider,
    { label: string; color: string; gradient: string }
  > = {
    email: { label: "Email", color: "bg-blue-500", gradient: "from-blue-500 to-blue-600" },
    google: { label: "Google", color: "bg-red-500", gradient: "from-red-500 to-red-600" },
    face: { label: "Face", color: "bg-purple-500", gradient: "from-accent-500 to-purple-600" },
  };

  const usersByAuthProvider = stats.users_by_auth_provider || {};
  const totalAuthUsers = Object.values(usersByAuthProvider).reduce(
    (sum, count) => sum + (count || 0),
    0
  );
  const jobsByType = stats.jobs_by_type || {};

  return (
    <div className="min-h-screen bg-navy-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20">
            <Shield className="w-6 h-6 text-primary-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-slate-400">Platform statistics and management</p>
          </div>
        </div>

        {/* User Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatsCard
            title="Total Users"
            value={formatNumber(stats.total_users || 0)}
            icon={<Users className="w-5 h-5" />}
            color="primary"
          />
          <StatsCard
            title="Active Users"
            value={formatNumber(stats.active_users || 0)}
            icon={<UserCheck className="w-5 h-5" />}
            color="success"
          />
          <StatsCard
            title="Engaged Users"
            value={formatNumber(stats.engaged_users || 0)}
            icon={<Flame className="w-5 h-5" />}
            color="warning"
            subtitle="Last 7 days"
          />
          <StatsCard
            title="Storage Used"
            value={formatFileSize((stats.total_storage_mb || 0) * 1024 * 1024)}
            icon={<HardDrive className="w-5 h-5" />}
            color="accent"
          />
        </div>

        {/* Job Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <StatsCard
            title="Total Jobs"
            value={formatNumber(stats.total_jobs || 0)}
            icon={<BarChart3 className="w-5 h-5" />}
            color="info"
          />
          <StatsCard
            title="Completed"
            value={formatNumber(stats.completed_jobs || 0)}
            icon={<CheckCircle2 className="w-5 h-5" />}
            color="success"
          />
          <StatsCard
            title="Processing"
            value={formatNumber(stats.processing_jobs || 0)}
            icon={<Zap className="w-5 h-5" />}
            color="warning"
          />
          <StatsCard
            title="Pending"
            value={formatNumber(stats.pending_jobs || 0)}
            icon={<Clock className="w-5 h-5" />}
            color="default"
          />
          <StatsCard
            title="Failed"
            value={formatNumber(stats.failed_jobs || 0)}
            icon={<XCircle className="w-5 h-5" />}
            color="danger"
          />
        </div>

        {/* Today's Activity */}
        <Card variant="glass" className="mb-8">
          <div className="px-6 py-4 border-b border-slate-700/50 flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-primary-400" />
            <h2 className="text-lg font-semibold text-white">Today's Activity</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                  {formatNumber(stats.jobs_today || 0)}
                </p>
                <p className="text-sm text-slate-400 mt-1">Jobs Today</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-400">
                  {formatNumber(stats.completed_jobs || 0)}
                </p>
                <p className="text-sm text-slate-400 mt-1">Completed</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-400">
                  {formatNumber(stats.processing_jobs || 0)}
                </p>
                <p className="text-sm text-slate-400 mt-1">In Progress</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-orange-400">
                  {formatNumber(stats.engaged_users || 0)}
                </p>
                <p className="text-sm text-slate-400 mt-1">Active This Week</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Users by Auth Provider */}
          <Card variant="glass">
            <div className="px-6 py-4 border-b border-slate-700/50">
              <h2 className="text-lg font-semibold text-white">
                Users by Authentication Method
              </h2>
            </div>
            <div className="p-6">
              {Object.keys(usersByAuthProvider).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(usersByAuthProvider).map(
                    ([provider, count]) => {
                      const config = authProviderLabels[provider as AuthProvider];
                      const safeCount = count || 0;
                      const percentage =
                        totalAuthUsers > 0
                          ? (safeCount / totalAuthUsers) * 100
                          : 0;

                      return (
                        <div key={provider} className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-300">
                              {config?.label || provider}
                            </span>
                            <span className="text-slate-400">
                              {formatNumber(safeCount)} ({percentage.toFixed(1)}%)
                            </span>
                          </div>
                          <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                            <div
                              className={`h-full bg-gradient-to-r ${
                                config?.gradient || "from-slate-500 to-slate-600"
                              } transition-all`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-4">
                  No data available
                </p>
              )}
            </div>
          </Card>

          {/* Jobs by Type */}
          <Card variant="glass">
            <div className="px-6 py-4 border-b border-slate-700/50">
              <h2 className="text-lg font-semibold text-white">Jobs by Type</h2>
            </div>
            <div className="p-6">
              {Object.keys(jobsByType).length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(jobsByType).map(([type, count]) => (
                    <div
                      key={type}
                      className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 text-center border border-slate-700/30 hover:border-slate-600/50 transition-all"
                    >
                      <p className="text-xl font-bold bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                        {formatNumber(count || 0)}
                      </p>
                      <p className="text-xs text-slate-400 mt-1 capitalize truncate">
                        {type.replace(/_/g, " ")}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-4">No jobs yet</p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
